import React, { useState, useMemo } from "react";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Badge, 
  ProgressBar,
  Alert
} from "react-bootstrap";
import {
  Cash,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  GraphUp,
  Clock,
  CheckCircle,
  XCircle,
  InfoCircle,
  FileText,
  Download,
  Printer,
  Calendar,
  Building,
  Wallet
} from "react-bootstrap-icons";
import { useAuth } from "../../contexts/AuthContext";

// Données fictives complètes (inchangées)
const mockReimbursements = [
  { 
    id: "RM-001", 
    patientId: "CNAMGS-123456", 
    prescriptionId: "ORD-001",
    pharmacyId: "PH001", 
    pharmacyName: "Pharmacie Centrale",
    amount: 15000, 
    date: "2025-03-12", 
    status: "pending",
    type: "Médicaments",
    prescriptionDate: "2025-03-10"
  },
  { 
    id: "RM-002", 
    patientId: "CNAMGS-123456", 
    prescriptionId: "ORD-002",
    pharmacyId: "PH002", 
    pharmacyName: "Pharmacie Moderne",
    amount: 2500, 
    date: "2025-03-05", 
    status: "approved",
    type: "Médicaments",
    prescriptionDate: "2025-03-01"
  },
  { 
    id: "RM-003", 
    patientId: "CNAMGS-123456", 
    prescriptionId: "ORD-003",
    pharmacyId: "PH003", 
    pharmacyName: "Pharmacie du Quartier",
    amount: 8000, 
    date: "2025-02-28", 
    status: "approved",
    type: "Consultation",
    prescriptionDate: "2025-02-25"
  },
  { 
    id: "RM-004", 
    patientId: "CNAMGS-123456", 
    prescriptionId: "ORD-004",
    pharmacyId: "PH001", 
    pharmacyName: "Pharmacie Centrale",
    amount: 3500, 
    date: "2025-02-20", 
    status: "rejected",
    type: "Médicaments",
    prescriptionDate: "2025-02-15",
    rejectionReason: "Pièce justificative manquante"
  },
  { 
    id: "RM-005", 
    patientId: "CNAMGS-123456", 
    prescriptionId: "ORD-005",
    pharmacyId: "PH004", 
    pharmacyName: "Pharmacie de la Gare",
    amount: 12000, 
    date: "2025-02-10", 
    status: "pending",
    type: "Analyse",
    prescriptionDate: "2025-02-05"
  },
  { 
    id: "RM-006", 
    patientId: "CNAMGS-123456", 
    prescriptionId: "ORD-006",
    pharmacyId: "PH002", 
    pharmacyName: "Pharmacie Moderne",
    amount: 4500, 
    date: "2025-01-28", 
    status: "approved",
    type: "Médicaments",
    prescriptionDate: "2025-01-25"
  },
  { 
    id: "RM-007", 
    patientId: "CNAMGS-123456", 
    prescriptionId: "ORD-007",
    pharmacyId: "PH001", 
    pharmacyName: "Pharmacie Centrale",
    amount: 20000, 
    date: "2025-01-15", 
    status: "approved",
    type: "Consultation",
    prescriptionDate: "2025-01-10"
  }
];

const ReimbursementStatus = () => {
  const { user } = useAuth();
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  // Utiliser les données fictives
  const patientReimbursements = mockReimbursements.filter(r => r.patientId === "CNAMGS-123456");

  // Statistiques
  const stats = useMemo(() => {
    const total = patientReimbursements.length;
    const pending = patientReimbursements.filter(r => r.status === 'pending').length;
    const approved = patientReimbursements.filter(r => r.status === 'approved').length;
    const rejected = patientReimbursements.filter(r => r.status === 'rejected').length;
    const totalAmount = patientReimbursements.reduce((sum, r) => sum + r.amount, 0);
    const approvedAmount = patientReimbursements
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + r.amount, 0);
    
    // Montants par type
    const medicationsAmount = patientReimbursements
      .filter(r => r.type === 'Médicaments' && r.status === 'approved')
      .reduce((sum, r) => sum + r.amount, 0);
    const consultationsAmount = patientReimbursements
      .filter(r => r.type === 'Consultation' && r.status === 'approved')
      .reduce((sum, r) => sum + r.amount, 0);
    const analysesAmount = patientReimbursements
      .filter(r => r.type === 'Analyse' && r.status === 'approved')
      .reduce((sum, r) => sum + r.amount, 0);
    
    return { total, pending, approved, rejected, totalAmount, approvedAmount, medicationsAmount, consultationsAmount, analysesAmount };
  }, [patientReimbursements]);

  const completionRate = stats.totalAmount > 0 ? Math.round((stats.approvedAmount / stats.totalAmount) * 100) : 0;

  // Filtrage
  const filteredReimbursements = patientReimbursements.filter(r => {
    const matchesSearch = filterText === '' || 
      r.pharmacyName.toLowerCase().includes(filterText.toLowerCase()) ||
      r.id.toLowerCase().includes(filterText.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchesType = typeFilter === 'all' || r.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Tri
  const sortedReimbursements = [...filteredReimbursements].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    if (sortField === 'date') {
      aValue = new Date(a.date);
      bValue = new Date(b.date);
    }
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge bg="warning" className="d-flex align-items-center gap-1 px-3 py-2 rounded-pill"><Clock size={12} /> En cours</Badge>,
      approved: <Badge bg="success" className="d-flex align-items-center gap-1 px-3 py-2 rounded-pill"><CheckCircle size={12} /> Remboursé</Badge>,
      rejected: <Badge bg="danger" className="d-flex align-items-center gap-1 px-3 py-2 rounded-pill"><XCircle size={12} /> Rejeté</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const getTypeIcon = (type) => {
    const icons = {
      Médicaments: <FileText size={12} className="me-1" />,
      Consultation: <Calendar size={12} className="me-1" />,
      Analyse: <GraphUp size={12} className="me-1" />
    };
    return icons[type] || <Wallet size={12} className="me-1" />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  // Types de prestations pour le filtre
  const types = [
    { value: "all", label: "Tous types" },
    { value: "Médicaments", label: "Médicaments" },
    { value: "Consultation", label: "Consultation" },
    { value: "Analyse", label: "Analyse" }
  ];

  // Cartes de statistiques stylisées
  const statsCards = [
    { 
      title: "Total demandes", 
      value: stats.total, 
      icon: FileText, 
      color: "#4361ee", 
      gradient: "linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)",
      bg: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)"
    },
    { 
      title: "En cours", 
      value: stats.pending, 
      icon: Clock, 
      color: "#f59e0b", 
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)"
    },
    { 
      title: "Remboursés", 
      value: stats.approved, 
      icon: CheckCircle, 
      color: "#10b981", 
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
    },
    { 
      title: "Montant total", 
      value: formatCurrency(stats.approvedAmount), 
      icon: Cash, 
      color: "#06b6d4", 
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      bg: "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)",
      subtitle: `sur ${formatCurrency(stats.totalAmount)}`
    }
  ];

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <ol className="breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><a href="/patient">Tableau de bord</a></li>
                <li className="breadcrumb-item active">Remboursements</li>
              </ol>
              <h4 className="main-title mb-0 d-flex align-items-center gap-2">
                <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                  <Cash size={24} className="text-primary" />
                </div>
                Suivi des remboursements
              </h4>
              <p className="text-muted mb-0 mt-1">
                Consultez l'état de vos demandes de remboursement
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" className="rounded-pill">
                <Download size={14} className="me-1" /> Exporter
              </Button>
              <Button variant="outline-secondary" size="sm" className="rounded-pill">
                <Printer size={14} className="me-1" /> Imprimer
              </Button>
            </div>
          </div>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-2 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm overflow-hidden stats-card h-100 bg-white" style={{ borderRadius: '14px' }}>
                <div className="stats-card-gradient" style={{ background: card.gradient, height: '3px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-0 small fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.3px' }}>
                        {card.title}
                      </h6>
                      <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem', lineHeight: 1.2 }}>
                        {card.value}
                      </h4>
                      <small className="text-muted mt-1 d-block" style={{ fontSize: '0.7rem' }}>
                        {card.subtitle || 'Total'}
                      </small>
                    </div>
                    <div className="stats-icon rounded-circle d-flex align-items-center justify-content-center" style={{ background: card.gradient, width: '40px', height: '40px' }}>
                      <card.icon size={20} className="text-white" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filtres */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '16px' }}>
              <Card.Body>
                <Row className="g-3">
                  <Col md={5} xs={6}>
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-0">
                        <Search size={16} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Rechercher par pharmacie ou numéro..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        className="border-0 bg-light"
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3} xs={6}>
                    <Form.Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="bg-light border-0"
                    >
                      {types.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2} xs={6}>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="bg-light border-0"
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="pending">En cours</option>
                      <option value="approved">Remboursés</option>
                      <option value="rejected">Rejetés</option>
                    </Form.Select>
                  </Col>
                  <Col md={2} xs={6}>
                    <Button 
                      variant="outline-secondary" 
                      className="w-100 rounded-pill"
                      onClick={() => {
                        setFilterText('');
                        setStatusFilter('all');
                        setTypeFilter('all');
                      }}
                    >
                      Réinitialiser
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tableau principal avec adaptation mobile */}
        <Row>
          <Col md={12}>
            <Card className="shadow-sm border-0 bg-white" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-primary text-white py-3">
                <div className="d-flex align-items-center gap-2">
                  <GraphUp size={20} />
                  <span className="fw-semibold">
                    Demandes de remboursement ({sortedReimbursements.length})
                  </span>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <SortableHeader field="date">Date</SortableHeader>
                        <SortableHeader field="pharmacyName">Pharmacie</SortableHeader>
                        <th className="d-none d-sm-table-cell">Type</th>
                        <th className="d-none d-sm-table-cell">Montant</th>
                        <th className="d-none d-sm-table-cell">Statut</th>
                        <th className="d-none d-sm-table-cell">Délai estimé</th>
                        <th className="d-sm-none">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedReimbursements.length > 0 ? (
                        sortedReimbursements.map(r => {
                          const isExpanded = expandedRows.has(r.id);
                          return (
                            <React.Fragment key={r.id}>
                              {/* Ligne principale */}
                              <tr className="align-middle">
                                <td data-label="Date" className="small">
                                  <div className="fw-medium">{formatDate(r.date)}</div>
                                  <small className="text-muted">N° {r.id}</small>
                                </td>
                                <td data-label="Pharmacie">
                                  <div className="d-flex align-items-center gap-2">
                                    <Building size={14} className="text-muted" />
                                    <span className="fw-medium">{r.pharmacyName}</span>
                                  </div>
                                </td>
                                <td data-label="Type" className="d-none d-sm-table-cell">
                                  <span className="d-flex align-items-center gap-1 text-muted small">
                                    {getTypeIcon(r.type)} {r.type}
                                  </span>
                                </td>
                                <td data-label="Montant" className="fw-bold text-success d-none d-sm-table-cell">
                                  {formatCurrency(r.amount)}
                                </td>
                                <td data-label="Statut" className="d-none d-sm-table-cell">
                                  {getStatusBadge(r.status)}
                                </td>
                                <td data-label="Délai" className="d-none d-sm-table-cell">
                                  {r.status === 'pending' && (
                                    <span className="d-flex align-items-center gap-1 text-muted small">
                                      <Clock size={12} /> 5-7 jours
                                    </span>
                                  )}
                                  {r.status === 'approved' && (
                                    <span className="d-flex align-items-center gap-1 text-success small">
                                      <CheckCircle size={12} /> Traité
                                    </span>
                                  )}
                                  {r.status === 'rejected' && (
                                    <span className="d-flex align-items-center gap-1 text-danger small">
                                      <XCircle size={12} /> {r.rejectionReason || 'Voir motif'}
                                    </span>
                                  )}
                                </td>
                                <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                                  <Button
                                    variant="link"
                                    onClick={() => toggleRow(r.id)}
                                    className="p-0 text-secondary"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </Button>
                                </td>
                              </tr>
                              {/* Ligne de détails (visible seulement sur mobile et si dépliée) */}
                              {isExpanded && (
                                <tr className="d-sm-none detail-row">
                                  <td colSpan="7" className="p-3 bg-light">
                                    <div className="row g-2">
                                      <div className="col-6"><strong>Type :</strong> {r.type}</div>
                                      <div className="col-6"><strong>Montant :</strong> {formatCurrency(r.amount)}</div>
                                      <div className="col-6"><strong>Statut :</strong> {getStatusBadge(r.status)}</div>
                                      <div className="col-6">
                                        <strong>Délai :</strong>
                                        {r.status === 'pending' && ' 5-7 jours'}
                                        {r.status === 'approved' && ' Traité'}
                                        {r.status === 'rejected' && (r.rejectionReason || ' Voir motif')}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <div className="bg-light rounded-circle p-3 d-inline-block mb-3">
                              <Cash size={48} className="text-muted" />
                            </div>
                            <p className="text-muted mb-0">Aucune demande de remboursement trouvée</p>
                            {(filterText || statusFilter !== 'all' || typeFilter !== 'all') && (
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="mt-2"
                                onClick={() => {
                                  setFilterText('');
                                  setStatusFilter('all');
                                  setTypeFilter('all');
                                }}
                              >
                                Réinitialiser les filtres
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <br />

        {/* Résumé des remboursements par type */}
        <Row className="g-3 mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '16px' }}>
              <Card.Body className="p-4">
                <h6 className="fw-semibold mb-3">Répartition par type de soins</h6>
                <Row className="g-3">
                  <Col md={4}>
                    <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: "#eef2ff" }}>
                      <div>
                        <small className="text-muted">Médicaments</small>
                        <div className="fw-bold">{formatCurrency(stats.medicationsAmount)}</div>
                      </div>
                      <FileText size={24} className="text-primary" />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: "#ecfdf5" }}>
                      <div>
                        <small className="text-muted">Consultations</small>
                        <div className="fw-bold">{formatCurrency(stats.consultationsAmount)}</div>
                      </div>
                      <Calendar size={24} className="text-success" />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: "#fffbeb" }}>
                      <div>
                        <small className="text-muted">Analyses</small>
                        <div className="fw-bold">{formatCurrency(stats.analysesAmount)}</div>
                      </div>
                      <GraphUp size={24} className="text-warning" />
                    </div>
                  </Col>
                </Row>
                <br />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <GraphUp size={20} className="text-success" />
                    <span className="fw-semibold">Progression des remboursements</span>
                  </div>
                  <span className="fw-bold text-success">{completionRate}%</span>
                </div>
                <ProgressBar 
                  now={completionRate} 
                  variant="success"
                  className="rounded-pill"
                  style={{ height: '8px' }}
                />
                <div className="d-flex justify-content-between mt-3">
                  <small className="text-muted">Montant remboursé: <strong>{formatCurrency(stats.approvedAmount)}</strong></small>
                  <small className="text-muted">Montant total: <strong>{formatCurrency(stats.totalAmount)}</strong></small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: "linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)" }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Informations sur les remboursements</strong>
                  <span className="small">Les remboursements sont effectués dans un délai de 5 à 7 jours ouvrés après validation. En cas de retard, veuillez contacter le service client au <strong className="text-primary">1414</strong>.</span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .stats-card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }
          .stats-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          .display-6 {
            font-size: 1.75rem;
            font-weight: 600;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 768px) {
            .stats-icon {
              width: 40px;
              height: 40px;
            }
            .stats-icon svg {
              width: 20px;
              height: 20px;
            }
            .display-6 {
              font-size: 1.5rem;
            }
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ReimbursementStatus;